import React, { useState, useEffect } from "react";
import {
  Package,
  Check,
  Heart,
  Loader2,
  ShieldCheck,
  ShoppingCart,
  Filter,
  Award,
  CreditCard,
  X,
} from "lucide-react";
import { doc, updateDoc, onSnapshot, arrayUnion } from "firebase/firestore";
import { db } from "../firebase";
import { useAppContext } from "../context/AppContext";

export default function Subs() {
  const { lang, user } = useAppContext();

  const [activeSubs, setActiveSubs] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [successPlan, setSuccessPlan] = useState<string | null>(null);

  const [activeFilter, setActiveFilter] = useState<
    "all" | "food" | "shelter" | "care"
  >("all");

  const [showMonoModal, setShowMonoModal] = useState(false);
  const [selectedPlanForPay, setSelectedPlanForPay] = useState<any | null>(
    null
  );

  // Слухаємо підписки користувача з Firebase
  useEffect(() => {
    if (!user) return;
    const unsub = onSnapshot(doc(db, "users", user.uid), (docSnap) => {
      if (docSnap.exists()) {
        setActiveSubs(docSnap.data().activeSubscriptions || []);
      }
    });
    return () => unsub();
  }, [user]);

  // Функція запуску процесу оплати
  const handleSubscribeClick = (plan: any) => {
    if (!user) return;
    setIsProcessing(true);
    setSelectedPlanForPay(plan);

    setTimeout(() => {
      setIsProcessing(false);
      setShowMonoModal(true);
    }, 1000);
  };

  // Емуляція натискання кнопки "Оплатити" на сторінці Монобанку
  const handleConfirmMonoPayment = async () => {
    if (!user || !selectedPlanForPay) return;
    setShowMonoModal(false);

    await updateDoc(doc(db, "users", user.uid), {
      activeSubscriptions: arrayUnion(selectedPlanForPay.id),
    });

    setSuccessPlan(selectedPlanForPay.id);
    setSelectedPlanForPay(null);
  };

  const plans = [
    {
      id: "shelter_donation_1",
      category: "shelter",
      name:
        lang === "uk" ? "Донат притулку: Базовий" : "Shelter Donation: Basic",
      price: 50,
      period: lang === "uk" ? "міс" : "mo",
      icon: Heart,
      iconColor: "text-[var(--red-text)] bg-[var(--red-soft)]",
      features: [
        lang === "uk"
          ? "Купівля смаколиків для одного хвостика"
          : "Treats for one homeless pet",
        lang === "uk"
          ? "Щомісячний фотозвіт від притулку"
          : "Monthly photo report",
      ],
    },
    {
      id: "shelter_donation_2",
      category: "shelter",
      name:
        lang === "uk" ? "Донат притулку: Опікун" : "Shelter Guardian Donation",
      price: 150,
      period: lang === "uk" ? "міс" : "mo",
      icon: Heart,
      iconColor: "text-[var(--red-text)] bg-[var(--red-soft)]",
      features: [
        lang === "uk"
          ? "Повне забезпечення медикаментами собаки"
          : "Full medical supplies for a dog",
        lang === "uk"
          ? "Особливий значок благодійника в Стрічці"
          : "Special benefactor badge in Feed",
        lang === "uk"
          ? "Щомісячний детальний звіт про витрати"
          : "Monthly transparency report",
      ],
    },
    {
      id: "food_subscription_small",
      category: "food",
      name:
        lang === "uk"
          ? "Підписка на корм: Малі породи"
          : "Food Delivery: Small Breeds",
      price: 399,
      period: lang === "uk" ? "міс" : "mo",
      icon: ShoppingCart,
      iconColor: "text-[#a073e7] bg-[var(--accent-soft)]",
      features: [
        lang === "uk"
          ? "Автодоставка сухого корму (до 3 кг)"
          : "Auto-delivery of dry food (up to 3kg)",
        lang === "uk"
          ? "Доставка під двері кожні 30 днів"
          : "Delivery to your door every 30 days",
        lang === "uk"
          ? "Фіксована ціна на весь період"
          : "Fixed price during subscription period",
      ],
    },
    {
      id: "food_subscription_large",
      category: "food",
      name:
        lang === "uk"
          ? "Підписка на корм: Великі породи"
          : "Food Delivery: Large Breeds",
      price: 799,
      period: lang === "uk" ? "міс" : "mo",
      icon: ShoppingCart,
      iconColor: "text-[#a073e7] bg-[var(--accent-soft)]",
      features: [
        lang === "uk"
          ? "Автодоставка преміум корму (10-12 кг)"
          : "Auto-delivery of premium food (10-12kg)",
        lang === "uk"
          ? "Гнучка зміна дати або марки корму"
          : "Flexible brand or date adjustments",
        lang === "uk"
          ? "Захист ціни від інфляції та стрибків"
          : "Price protected from inflation",
      ],
      popular: true,
    },
    {
      id: "care_bundle_premium",
      category: "care",
      name: lang === "uk" ? "Комплексний Набір Турботи" : "Full Care Bundle",
      price: 1199,
      period: lang === "uk" ? "міс" : "mo",
      icon: Package,
      iconColor: "text-[#10b981] bg-[#10b981]/10",
      features: [
        lang === "uk"
          ? "Місячний раціон корму + вітаміни"
          : "Monthly food ration + vitamins pack",
        lang === "uk"
          ? "Засоби захисту від бліх та кліщів"
          : "Flea & tick protection treatments",
        lang === "uk"
          ? "Безкоштовний чат з ветеринаром 24/7"
          : "Free 24/7 online chat with veterinarian",
      ],
    },
  ];

  const filteredPlans = plans.filter(
    (p) => activeFilter === "all" || p.category === activeFilter
  );

  return (
    <div className="p-5 flex flex-col min-h-screen bg-[var(--bg-soft)] pb-32 animate-fade-in">
      {successPlan && (
        <div
          className="fixed inset-0 bg-black/70 z-[999] flex items-center justify-center p-6 backdrop-blur-sm"
          onClick={() => setSuccessPlan(null)}
        >
          <div
            className="bg-[var(--bg-card)] rounded-[2.5rem] p-6 text-center border-2 border-[#10b981] shadow-2xl max-w-xs flex flex-col items-center animate-scale-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-16 h-16 bg-[#10b981]/10 text-[#10b981] rounded-full flex items-center justify-center mb-4">
              <ShieldCheck size={36} />
            </div>
            <h3 className="font-black text-2xl text-[var(--text-main)] mb-2">
              {lang === "uk" ? "Успішно!" : "Success!"}
            </h3>
            <p className="text-xs text-[var(--text-muted)] leading-relaxed mb-6">
              {lang === "uk"
                ? "Вашу регулярну підписку успішно оформлено та оплачено!"
                : "Your recurring subscription is now fully active!"}
            </p>
            <button
              onClick={() => setSuccessPlan(null)}
              className="w-full py-3 bg-[#10b981] text-white font-bold rounded-xl active:scale-95 transition-all text-sm shadow-md"
            >
              Чудово
            </button>
          </div>
        </div>
      )}

      {showMonoModal && selectedPlanForPay && (
        <div className="fixed inset-0 bg-neutral-900/90 z-[999] flex items-center justify-center p-4 backdrop-blur-sm animate-fade-in">
          <div className="bg-[#1e1e24] text-white rounded-[2rem] w-full max-w-sm overflow-hidden shadow-2xl border border-neutral-800 flex flex-col animate-scale-up">
            <div className="bg-[#e65a5a] p-5 text-center shrink-0 flex flex-col items-center relative">
              <button
                onClick={() => setShowMonoModal(false)}
                className="absolute top-4 right-4 bg-black/20 text-white rounded-full p-1.5"
              >
                <X size={16} />
              </button>
              <img
                src="https://api.monobank.ua/favicon.ico"
                alt="mono"
                className="w-8 h-8 rounded-full bg-white p-1 mb-2 shadow"
              />
              <h3 className="font-black text-lg">monobank | acquirring</h3>
            </div>

            <div className="p-6 flex-1 flex flex-col gap-5 text-center">
              <div>
                <p className="text-neutral-400 text-xs font-bold uppercase tracking-wider">
                  Призначення платежу
                </p>
                <h4 className="font-bold text-sm text-neutral-200 mt-1">
                  Підписка MyPaws: {selectedPlanForPay.name}
                </h4>
              </div>

              <div className="bg-neutral-800/40 border border-neutral-800 rounded-2xl p-4">
                <span className="text-neutral-400 text-xs font-bold block mb-1">
                  Сума до сплати
                </span>
                <span className="text-3xl font-black text-white">
                  {selectedPlanForPay.price} ₴
                </span>
              </div>

              <div className="flex flex-col gap-2.5 mt-2">
                <button
                  onClick={handleConfirmMonoPayment}
                  className="w-full h-12 bg-black text-white font-black rounded-xl text-sm flex items-center justify-center gap-2 active:scale-95 transition-all shadow-md"
                >
                   Pay
                </button>
                <button
                  onClick={handleConfirmMonoPayment}
                  className="w-full h-12 bg-white text-black font-black rounded-xl text-sm flex items-center justify-center gap-2 active:scale-95 transition-all shadow-md border border-neutral-300"
                >
                  <img
                    src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                    width="16"
                    alt="G"
                  />{" "}
                  Google Pay
                </button>

                <div className="flex items-center my-1 text-neutral-500 text-[10px] uppercase font-bold tracking-widest">
                  <div className="flex-1 h-px bg-neutral-800 mr-2"></div> або
                  карткою{" "}
                  <div className="flex-1 h-px bg-neutral-800 ml-2"></div>
                </div>

                <button
                  onClick={handleConfirmMonoPayment}
                  className="w-full h-12 bg-[#e65a5a] text-white font-bold rounded-xl text-sm flex items-center justify-center gap-2 active:scale-95 transition-all shadow-md"
                >
                  <CreditCard size={16} /> Оплатити карткою
                </button>
              </div>
            </div>
            <div className="bg-neutral-900/60 p-4 text-center border-t border-neutral-800/50 text-[10px] text-neutral-500 font-medium">
              Secure Payment Gateway powered by Monobank
            </div>
          </div>
        </div>
      )}

      <div className="text-center pt-8 mb-6 shrink-0">
        <h2 className="font-black text-2xl text-[var(--text-main)]">
          {lang === "uk" ? "Підписки MyPaws" : "MyPaws Subscriptions"}
        </h2>
        <p className="text-xs text-[var(--text-muted)] mt-1.5 max-w-xs mx-auto leading-relaxed">
          {lang === "uk"
            ? "Керуйте регулярним забезпеченням вашої тваринки або підтримуйте притулки"
            : "Manage regular supplies for your pet or support shelters"}
        </p>
      </div>

      <div className="flex gap-1.5 bg-[var(--bg-card)] border border-[var(--border-color)] p-1 rounded-2xl mb-6 shrink-0 max-w-md mx-auto w-full overflow-x-auto no-scrollbar">
        <button
          onClick={() => setActiveFilter("all")}
          className={`flex-1 py-2 px-3 rounded-xl text-xs font-black uppercase tracking-wide transition-all whitespace-nowrap ${
            activeFilter === "all"
              ? "bg-[var(--inverted-btn)] text-[var(--inverted-text)] shadow-sm"
              : "text-[var(--text-muted)]"
          }`}
        >
          {lang === "uk" ? "Всі" : "All"}
        </button>
        <button
          onClick={() => setActiveFilter("food")}
          className={`flex-1 py-2 px-3 rounded-xl text-xs font-black uppercase tracking-wide transition-all whitespace-nowrap ${
            activeFilter === "food"
              ? "bg-[#a073e7] text-white shadow-sm"
              : "text-[var(--text-muted)]"
          }`}
        >
          {lang === "uk" ? "Корм" : "Food"}
        </button>
        <button
          onClick={() => setActiveFilter("shelter")}
          className={`flex-1 py-2 px-3 rounded-xl text-xs font-black uppercase tracking-wide transition-all whitespace-nowrap ${
            activeFilter === "shelter"
              ? "bg-[var(--red-text)] text-white shadow-sm"
              : "text-[var(--text-muted)]"
          }`}
        >
          {lang === "uk" ? "Притулки" : "Shelters"}
        </button>
        <button
          onClick={() => setActiveFilter("care")}
          className={`flex-1 py-2 px-3 rounded-xl text-xs font-black uppercase tracking-wide transition-all whitespace-nowrap ${
            activeFilter === "care"
              ? "bg-[#10b981] text-white shadow-sm"
              : "text-[var(--text-muted)]"
          }`}
        >
          {lang === "uk" ? "Догляд" : "Care"}
        </button>
      </div>

      <div className="flex flex-col gap-5 max-w-sm mx-auto w-full flex-1 overflow-y-auto no-scrollbar">
        {filteredPlans.length === 0 && (
          <div className="text-center py-10 text-[var(--text-muted)] font-bold text-sm">
            У цій категорії підписок немає
          </div>
        )}
        {filteredPlans.map((plan) => {
          const isPlanActive = activeSubs.includes(plan.id);
          const PlanIcon = plan.icon;

          return (
            <div
              key={plan.id}
              className={`bg-[var(--bg-card)] rounded-3xl p-5 border relative shadow-sm flex flex-col transition-all ${
                plan.popular && !isPlanActive
                  ? "border-[#a073e7] ring-2 ring-[#a073e7]/10"
                  : "border-[var(--border-color)]"
              } ${
                isPlanActive
                  ? "opacity-95 border-[#10b981] bg-gradient-to-b from-[var(--bg-card)] to-[#10b981]/5"
                  : ""
              }`}
            >
              {plan.popular && !isPlanActive && (
                <span className="absolute -top-3 right-6 bg-[#a073e7] text-white text-[9px] font-black uppercase tracking-wider px-3 py-1 rounded-full shadow-sm">
                  {lang === "uk" ? "Популярно" : "Popular"}
                </span>
              )}
              {isPlanActive && (
                <span className="absolute -top-3 right-6 bg-[#10b981] text-white text-[9px] font-black uppercase tracking-wider px-3 py-1 rounded-full shadow-sm">
                  {lang === "uk" ? "Активна" : "Active"}
                </span>
              )}

              <div className="flex gap-3 items-center mb-3">
                <div
                  className={`w-10 h-10 rounded-xl flex items-center justify-center ${plan.iconColor}`}
                >
                  <PlanIcon size={20} />
                </div>
                <div className="min-w-0 flex-1">
                  <h4 className="font-black text-sm text-[var(--text-main)] leading-tight truncate">
                    {plan.name}
                  </h4>
                  <p className="text-xl font-black text-[var(--text-main)] mt-0.5">
                    {plan.price} ₴
                    <span className="text-xs font-bold text-[var(--text-muted)]">
                      {" "}
                      / {plan.period}
                    </span>
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-2.5 my-3 border-t border-[var(--border-color)] pt-3 flex-1">
                {plan.features.map((f, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-2 text-xs text-[var(--text-main)] font-medium"
                  >
                    <div className="w-4 h-4 rounded-full bg-[#10b981]/10 text-[#10b981] flex items-center justify-center shrink-0 mt-0.5">
                      <Check size={10} strokeWidth={3} />
                    </div>
                    <span className="flex-1 leading-snug">{f}</span>
                  </div>
                ))}
              </div>

              <button
                onClick={() => handleSubscribeClick(plan)}
                disabled={isProcessing || isPlanActive}
                className={`w-full h-11 rounded-xl font-bold text-xs flex items-center justify-center gap-2 active:scale-95 transition-all shadow-md mt-2 ${
                  isPlanActive
                    ? "bg-[#10b981]/10 text-[#10b981] border border-[#10b981]/20 shadow-none cursor-default font-black"
                    : plan.popular
                    ? "bg-[#a073e7] text-white"
                    : "bg-[var(--inverted-btn)] text-[var(--inverted-text)]"
                } disabled:opacity-50`}
              >
                {isProcessing && <Loader2 size={16} className="animate-spin" />}
                {isPlanActive
                  ? lang === "uk"
                    ? "Підписка оформлена ✓"
                    : "Subscribed ✓"
                  : isProcessing
                  ? lang === "uk"
                    ? "Обробка..."
                    : "Processing..."
                  : lang === "uk"
                  ? "Оформити через monobank"
                  : "Subscribe Now"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
